import React, { useContext, useEffect, useRef, useState } from 'react';
import { CanvasContext } from 'components/upload-image/CanvasContext';
import useTooltipSequence from 'components/useTooltipSequence';
import useRegisterTooltipTarget from 'components/useRegisterTooltipTarget';
import FeatureTooltip from 'components/FeatureToolTip';
import { STICKERS_PANEL_TOOLTIPS } from 'constants/stickersTooltips';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

/**
 * Panel component: choose which sticker to place next.
 * (No delete/deselect buttons here anymore.)
 */
export default function StickersTool({ isActive }) {
  const { selectedStickerSrc, setSelectedStickerSrc, activeTool } =
    useContext(CanvasContext);
  const [stickers, setStickers] = useState([]);

  /* TOOLTIP FEATURE */
  const stickersToolRef = useRef(null);

  const stickersTooltipSequence = useTooltipSequence(STICKERS_PANEL_TOOLTIPS, {
    enabled: isActive && activeTool === 'stickers',
    initialDelay: 500,
    stepDelay: 1800,
    visibilityThreshold: 0.8,
    visibilityDelay: 300,
  });
  useRegisterTooltipTarget(
    stickersTooltipSequence,
    'stickers-tool',
    stickersToolRef,
  );

  const withPublicUrl = (path = '') =>
    path.startsWith('http') ? path : `${process.env.PUBLIC_URL || ''}${path}`;

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/images/stickers/stickers.manifest.json`)
      .then((r) => r.json())
      .then((data) => setStickers(Array.isArray(data) ? data : []))
      .catch(() => setStickers([]));
  }, []);

  const hasStickers = stickers.length > 0;

  if (!isActive) return null;

  return (
    <div className="w-full px-4 py-3">
      <div
        className={[
          'w-full',
          'overflow-x-auto overflow-y-hidden',
          'touch-pan-x',
          'overscroll-y-none',
          'pb-3 lg:pb-0',
          'snap-x snap-mandatory',
          'scroll-px-4',
        ].join(' ')}
      >
        {!hasStickers && (
          <p className="mt-2 text-xs text-stone-500">
            No stickers found. Check your stickers.manifest.json file exists in
            /public/images/stickers/.
          </p>
        )}

        <div
          ref={stickersToolRef}
          className="flex lg:flex-wrap gap-6 w-max mx-auto"
        >
          {stickers.map((s) => {
            const isActiveSticker = selectedStickerSrc === s.src;

            return (
              <button
                key={s.id}
                type="button"
                onClick={() =>
                  setSelectedStickerSrc((prev) =>
                    prev === s.src ? null : s.src,
                  )
                }
                className={`rounded-xl border bg-white/70 p-1 flex items-center justify-center shadow-sm hover:bg-white transition ${
                  isActiveSticker ? 'border-stone-900' : 'border-stone-200'
                }`}
                title={`Select ${s.name}`}
              >
                <img
                  src={withPublicUrl(s.src)}
                  alt={s.name}
                  className="h-8 w-8"
                  draggable={false}
                  onError={() => {
                    // eslint-disable-next-line no-console
                    console.warn(`Sticker not found: ${s.src}`);
                  }}
                />
              </button>
            );
          })}
        </div>

        {/* <p className="mt-2 text-xs text-stone-700 text-center">
          Select a sticker, then click on the image to place it. Tap a placed
          sticker to select it.
        </p> */}
        {stickersTooltipSequence.activeTooltip && (
          <FeatureTooltip
            targetRef={stickersTooltipSequence.activeTooltip.ref}
            open={true}
            placement={stickersTooltipSequence.activeTooltip.placement || 'top'}
            align={stickersTooltipSequence.activeTooltip.align || 'center'}
            title={stickersTooltipSequence.activeTooltip.title}
            description={stickersTooltipSequence.activeTooltip.description}
            buttonLabel="Got it"
            onClose={() =>
              stickersTooltipSequence.closeTooltip(
                stickersTooltipSequence.activeTooltip.id,
              )
            }
          />
        )}
      </div>
    </div>
  );
}

/**
 * Overlay layer: render placed stickers on top of the canvas,
 * with click-to-select, delete button, resize handle, and rotate handle.
 */
export function StickersLayer() {
  const {
    activeTool,
    transformMode,
    overlayRef,
    scaleRef,
    stickerItems,
    setStickerItems,
    selectedStickerId,
    setSelectedStickerId,
    saveHistory,
  } = useContext(CanvasContext);

  const isRotateMode = activeTool === 'transform' && transformMode === 'rotate';
  const isStickerMode = activeTool === 'stickers' && !isRotateMode;

  const dragStateRef = useRef(null);
  const resizeStateRef = useRef(null);
  const rotateStateRef = useRef(null);

  const getPointerCanvasPos = (clientX, clientY) => {
    // const overlay = overlayRef.current;
    // if (!overlay) return null;

    const rect = overlay.getBoundingClientRect();
    const sx = scaleRef.current?.x || 1;
    const sy = scaleRef.current?.y || 1;

    return {
      x: (clientX - rect.left) * sx,
      y: (clientY - rect.top) * sy,
    };
  };

  const beginDrag = (e, stickerId) => {
    if (!isStickerMode) return;
    if (e?.target?.dataset?.stickerControl === '1') return;

    saveHistory();

    e.preventDefault();
    e.stopPropagation();

    const p = getPointerCanvasPos(e.clientX, e.clientY);
    if (!p) return;

    const sticker = (Array.isArray(stickerItems) ? stickerItems : []).find(
      (s) => s?.id === stickerId,
    );
    if (!sticker) return;

    setSelectedStickerId(stickerId);

    dragStateRef.current = {
      id: stickerId,
      offsetX: p.x - (sticker.x || 0),
      offsetY: p.y - (sticker.y || 0),
    };

    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      const state = dragStateRef.current;
      if (!state) return;

      const pp = getPointerCanvasPos(ev.clientX, ev.clientY);
      if (!pp) return;

      setStickerItems((items) =>
        (Array.isArray(items) ? items : []).map((s) =>
          s?.id === state.id
            ? { ...s, x: pp.x - state.offsetX, y: pp.y - state.offsetY }
            : s,
        ),
      );
    };

    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const beginResize = (e, sticker) => {
    if (!isStickerMode) return;

    saveHistory();

    e.preventDefault();
    e.stopPropagation();

    const p = getPointerCanvasPos(e.clientX, e.clientY);
    if (!p) return;

    const cx = sticker.x || 0;
    const cy = sticker.y || 0;

    const startDist = Math.max(1, Math.hypot(p.x - cx, p.y - cy));
    const startW = sticker.w || 140;
    const startH = sticker.h || 140;

    setSelectedStickerId(sticker.id);

    resizeStateRef.current = {
      id: sticker.id,
      cx,
      cy,
      startDist,
      startW,
      startH,
    };

    const onMove = (ev) => {
      const st = resizeStateRef.current;
      if (!st) return;

      const pp = getPointerCanvasPos(ev.clientX, ev.clientY);
      if (!pp) return;

      const dist = Math.max(1, Math.hypot(pp.x - st.cx, pp.y - st.cy));
      const scale = dist / st.startDist;

      const nextW = clamp(Math.round(st.startW * scale), 24, 2000);
      const nextH = clamp(Math.round(st.startH * scale), 24, 2000);

      setStickerItems((items) =>
        (Array.isArray(items) ? items : []).map((s) =>
          s?.id === st.id ? { ...s, w: nextW, h: nextH } : s,
        ),
      );
    };

    const onUp = () => {
      resizeStateRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const snapToRightAngle = (deg, threshold = 8) => {
    const snapped = Math.round(deg / 90) * 90;
    return Math.abs(deg - snapped) <= threshold ? snapped : deg;
  };

  const beginRotate = (e, sticker) => {
    if (!isStickerMode) return;

    saveHistory();

    e.preventDefault();
    e.stopPropagation();

    const p = getPointerCanvasPos(e.clientX, e.clientY);
    if (!p) return;

    const cx = sticker.x || 0;
    const cy = sticker.y || 0;

    const startPointerAngle = Math.atan2(p.y - cy, p.x - cx);
    const startRot = Number(sticker.rotation || 0);

    setSelectedStickerId(sticker.id);

    rotateStateRef.current = {
      id: sticker.id,
      cx,
      cy,
      startPointerAngle,
      startRot,
    };

    const onMove = (ev) => {
      const st = rotateStateRef.current;
      if (!st) return;

      const pp = getPointerCanvasPos(ev.clientX, ev.clientY);
      if (!pp) return;

      const a = Math.atan2(pp.y - st.cy, pp.x - st.cx);
      const delta = a - st.startPointerAngle;

      let nextRot = st.startRot + (delta * 180) / Math.PI;
      nextRot = snapToRightAngle(nextRot, 8);

      setStickerItems((items) =>
        (Array.isArray(items) ? items : []).map((s) =>
          s?.id === st.id ? { ...s, rotation: nextRot } : s,
        ),
      );
    };

    const onUp = () => {
      rotateStateRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const deleteSticker = (id) => {
    saveHistory();

    setStickerItems((items) =>
      (Array.isArray(items) ? items : []).filter((s) => s?.id !== id),
    );
    setSelectedStickerId((cur) => (cur === id ? null : cur));
  };

  const overlay = overlayRef.current;
  if (!overlay) return null;

  const sx = scaleRef.current?.x || 1;
  const sy = scaleRef.current?.y || 1;

  return (
    <>
      {(Array.isArray(stickerItems) ? stickerItems : []).map((s) => {
        if (!s?.id || !s?.src) return null;

        const w = typeof s.w === 'number' ? s.w : 140;
        const h = typeof s.h === 'number' ? s.h : 140;
        const x = typeof s.x === 'number' ? s.x : 0;
        const y = typeof s.y === 'number' ? s.y : 0;
        const rotation = Number(s.rotation || 0);

        const left = (x - w / 2) / sx;
        const top = (y - h / 2) / sy;
        const width = w / sx;
        const height = h / sy;

        const isSelected = selectedStickerId === s.id;

        return (
          <div
            key={s.id}
            data-sticker-item="1"
            className={`absolute group ${
              isSelected && isStickerMode
                ? 'border-2 border-sky-500 border-dashed shadow-sm'
                : isStickerMode
                  ? 'border-2 border-transparent hover:border-stone-400 hover:border-dashed'
                  : 'border-2 border-transparent'
            }`}
            style={{
              left,
              top,
              width,
              height,
              zIndex: isSelected ? 30 : 20,
              touchAction: 'none',
              userSelect: 'none',
              overflow: 'visible',
              boxSizing: 'border-box',
              pointerEvents: isRotateMode
                ? 'none'
                : isStickerMode
                  ? 'auto'
                  : 'none',
              cursor: isStickerMode ? 'grab' : 'default',

              // ✅ rotate the WHOLE wrapper (border + handles + image)
              transform: `scaleX(${s.flipX ? -1 : 1}) scaleY(${s.flipY ? -1 : 1}) rotate(${rotation}deg)`,
              transformOrigin: '50% 50%',
            }}
            onPointerDown={(e) => beginDrag(e, s.id)}
            onClick={(e) => {
              if (!isStickerMode) return;
              e.stopPropagation();
              setSelectedStickerId(s.id);
            }}
            onMouseEnter={() => {
              if (isStickerMode) document.body.style.cursor = 'grab';
            }}
            onMouseLeave={() => {
              if (
                !dragStateRef.current &&
                !resizeStateRef.current &&
                !rotateStateRef.current
              ) {
                document.body.style.cursor = '';
              }
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/${s.src}`}
              alt="Sticker"
              draggable={false}
              className="w-full h-full"
              // IMPORTANT: no rotate here anymore
              style={{ pointerEvents: 'none' }}
            />

            {/* Delete control */}
            {isStickerMode && isSelected && (
              <button
                type="button"
                data-sticker-control="1"
                aria-label="Delete sticker"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteSticker(s.id);
                }}
                className="absolute z-50 h-7 w-7 rounded-full bg-white/90 shadow hover:bg-white border border-stone-200 flex items-center justify-center"
                style={{
                  right: '-18px',
                  top: '-18px',
                  cursor: 'pointer',
                }}
                title="Delete"
              >
                <span
                  style={{ fontSize: 16, lineHeight: 1, userSelect: 'none' }}
                >
                  ×
                </span>
              </button>
            )}

            {/* Resize handle */}
            {isStickerMode && isSelected && (
              <button
                type="button"
                data-sticker-control="1"
                aria-label="Resize sticker"
                onPointerDown={(e) => beginResize(e, s)}
                className="absolute z-50 h-7 w-7 rounded-full bg-white/90 shadow hover:bg-white border border-stone-200 flex items-center justify-center"
                style={{
                  right: '-18px',
                  bottom: '-18px',
                  cursor: 'nwse-resize',
                  touchAction: 'none',
                }}
                title="Resize"
              >
                <span
                  style={{ fontSize: 14, lineHeight: 1, userSelect: 'none' }}
                >
                  ↔
                </span>
              </button>
            )}

            {/* Rotate handle */}
            {isStickerMode && isSelected && (
              <button
                type="button"
                data-sticker-control="1"
                aria-label="Rotate sticker"
                onPointerDown={(e) => beginRotate(e, s)}
                className="absolute z-50 h-7 w-7 rounded-full bg-white/90 shadow hover:bg-white border border-stone-200 flex items-center justify-center"
                style={{
                  left: '50%',
                  top: '-18px',
                  transform: 'translateX(-50%)',
                  cursor: 'grab',
                  touchAction: 'none',
                }}
                title="Rotate"
              >
                <span
                  style={{ fontSize: 14, lineHeight: 1, userSelect: 'none' }}
                >
                  ↻
                </span>
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}
